import { ClipboardCheck, Ruler, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { teamWorkerName } from '@obraplan/core/domain/planning/daily-report-task';
import { Hours } from '@obraplan/core/domain/shared/hours';
import { isAreaUnitCode, roundQuantity } from '@obraplan/core/domain/schedule/unit-of-measure';
import type {
  TeamFrontView,
  TeamOfDayView,
} from '@obraplan/core/application/contracts/team-measurement';
import type { DayQualityView } from '@obraplan/core/application/use-cases/quality/quality-use-cases';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, Input } from '@/components/ui/input';
import { AreaMeasurementButton } from './AreaMeasurementDialog';
import { PecasDoDia, type CarregarPecas } from './PecasDoDia';
import { QualityVerificationStep, type NonConformityPhotos } from './QualityVerificationStep';
import type { StepMeasurement } from './NonConformityFields';
import { TeamFrontMembers } from './TeamFrontMembers';
import {
  frontDraftToInput,
  NEW_MEASUREMENT_OWNER,
  pendingPhotos,
  type PendingNonConformityPhotos,
  frontHasVerification,
  initialFrontDraft,
  isFrontDraftComplete,
  summarizeFrontDraft,
  type FrontDraft,
  type VerificationInput,
} from './quality-verification-draft';
import { describeTeamMeasurement, formatTeamQuantity } from './team-measurement-format';

/**
 * A medição de uma equipe numa frente × serviço, numa caixa só (Fase 30, etapa 1 — D1, D7).
 *
 * É a irmã da medição da pessoa (`DayMeasurementDialog`), com uma diferença que muda tudo: a
 * quantidade é **da equipe**, lançada uma vez, e quem trabalhou aparece só para leitura — as
 * horas, a ausência e a hora extra seguem nos cartões de cada pessoa. Por isso aqui não há
 * ajuste de horas nem confirmação do dia: a caixa mede, verifica e fotografa.
 *
 * ## A checklist é a de sempre
 *
 * A quantidade nova só grava com a verificação de qualidade (RN-218), e o segundo passo é o
 * mesmo `QualityVerificationStep` da medição da pessoa — a checklist é da frente × serviço ×
 * dia, e não muda com o modo da obra. A diferença é que o serviço já vem escolhido: a equipe
 * mede **um** serviço nesta caixa, e a pergunta "qual serviço executou hoje?" já foi
 * respondida. Por isso a frente é recortada ao serviço medido: as respostas e o que se envia
 * são só dele, ainda que a pessoa tenha verificado outro serviço da mesma frente hoje.
 *
 * Como na medição da pessoa, **só o que mudou é enviado**: regravar o mesmo número não é
 * medição nova e não pede a checklist de novo; uma frente já verificada hoje, por quem quer que
 * seja (ADR-128), cobre a quantidade nova sem responder outra vez.
 *
 * ## O que a página liga
 *
 * Componente de apresentação. `quality` é o mesmo `DayQualityView` que a medição da pessoa já
 * lê (`undefined` enquanto carrega — e aí quantidade nova não grava), `photos` é a galeria da
 * medição da equipe (D7) montada por quem chama, e `onConfirm` leva a quantidade e as
 * verificações no formato do `RecordTeamMeasurement`.
 */
export interface TeamMeasurementConfirmation {
  /** `null` tira a medição do dia. */
  readonly quantity: number | null;
  readonly verifications: readonly VerificationInput[];
  /**
   * As peças marcadas no dia (RN-413, RN-414), presente só quando a pessoa mexeu na lista:
   * quem grava as marca **antes** da quantidade, e uma recusa (peça já executada em outro dia)
   * para a gravação.
   */
  readonly pieceNames?: readonly string[];
  /** As que estavam marcadas no dia quando a pessoa abriu a lista (Fase 43, RN-615). */
  readonly previousPieceNames?: readonly string[];
  /** As fotos escolhidas nos itens reprovados, para subir depois de gravar (Fase 37, RN-479). */
  readonly photos?: readonly PendingNonConformityPhotos[];
}

export interface TeamMeasurementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** A equipe medida — o id chaveia o rascunho: duas equipes podem medir a mesma frente × serviço. */
  team: Pick<TeamOfDayView, 'teamId' | 'teamName'> | undefined;
  front: TeamFrontView | undefined;
  dateLabel: string;
  /** A qualidade do dia. `undefined` enquanto carrega — e aí não se grava quantidade nova. */
  quality: DayQualityView | undefined;
  /** As fotos das NCs já abertas. Ausente = sem galeria no passo da checklist. */
  nonConformityPhotos?: NonConformityPhotos | undefined;
  /** As fotos da medição da equipe (D7): a galeria é montada por quem chama. */
  photos?: ReactNode;
  /**
   * As peças do dia (Fase 28, etapa 2 — RN-414): a frente × serviço que tem peça, da planilha
   * ou do desenho, mostra a lista sob a quantidade, como na medição da pessoa. Ausente = sem
   * lista.
   */
  pieces?: { readonly date: string; readonly load: CarregarPecas } | undefined;
  isSaving: boolean;
  errorMessage?: string | undefined;
  onConfirm: (result: TeamMeasurementConfirmation) => void;
}

/** O teto do contrato (`recordTeamMeasurementSchema`). */
const QUANTIDADE_MAXIMA = 1_000_000_000;

const SEM_RESPOSTAS = { approved: 0, rejected: 0, total: 0 } as const;

export function TeamMeasurementDialog({
  open,
  onOpenChange,
  team,
  front,
  ...resto
}: TeamMeasurementDialogProps) {
  return (
    <Dialog open={open && team !== undefined && front !== undefined} onOpenChange={onOpenChange}>
      {/*
        Coluna flex: o miolo rola e o rodapé fica à vista. O conteúdo é um componente à parte,
        montado quando a caixa abre: os rascunhos nascem do que está gravado e morrem ao fechar,
        sem efeito de reinício — e uma foto anexada, que revalida a tela, não apaga a quantidade.
        A gravação, sim, recomeça a caixa: a página a deixa aberta para as fotos, e o rascunho
        da checklist, já gravado, continuaria "a gravar" e pronto para ser enviado de novo.
      */}
      <DialogContent className="flex max-w-2xl flex-col">
        {team !== undefined && front !== undefined && (
          <Conteudo
            key={`${team.teamId}|${front.activityId}|${front.serviceId}|${front.measurement?.measuredAt ?? ''}`}
            team={team}
            front={front}
            onOpenChange={onOpenChange}
            {...resto}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

type Passo = 'medir' | 'verificar';

function Conteudo({
  onOpenChange,
  team,
  front,
  dateLabel,
  quality,
  nonConformityPhotos,
  photos,
  pieces,
  isSaving,
  errorMessage,
  onConfirm,
}: Omit<TeamMeasurementDialogProps, 'open' | 'team' | 'front'> & {
  team: Pick<TeamOfDayView, 'teamId' | 'teamName'>;
  front: TeamFrontView;
}) {
  const unidade = front.unit;
  const medida = front.measurement;

  // Abre com o que está gravado, ou com a **meta** da equipe quando ainda não houve medição: no
  // dia comum saiu o combinado, e redigitar o número que já está na tela é o caminho mais curto
  // para ninguém medir. Quem rendeu diferente sobrescreve.
  const [valor, setValor] = useState(() => {
    if (medida !== undefined) return String(medida.quantity);
    if (front.plannedQuantity === undefined) return '';
    return String(roundQuantity(front.plannedQuantity, unidade?.decimals ?? 3));
  });
  // Só existe depois que a pessoa mexeu na checklist; até lá o rascunho é derivado do que está
  // gravado — e isso resolve a qualidade que chega depois de a caixa abrir.
  const [rascunhoEditado, setRascunhoEditado] = useState<FrontDraft | undefined>(undefined);
  const [passo, setPasso] = useState<Passo>('medir');
  const [removendo, setRemovendo] = useState(false);
  // As peças marcadas — só depois que a pessoa mexeu; até lá, as que o servidor tem no dia.
  const [pecas, setPecas] = useState<readonly string[] | undefined>(undefined);
  // As que já estavam no dia quando a lista abriu: a fila manda só a diferença (RN-615).
  const [pecasVistas, setPecasVistas] = useState<readonly string[]>([]);
  // O que as peças puseram no campo: desmarcar todas zera só isso, e o número digitado à mão
  // continua sendo de quem digitou.
  const [doPreenchimento, setDoPreenchimento] = useState<string | undefined>(undefined);

  // A frente recortada ao serviço medido: a checklist, o rascunho e o envio são só dele.
  const frenteDaQualidade = quality?.fronts.find((item) => item.activityId === front.activityId);
  const frente =
    frenteDaQualidade === undefined
      ? undefined
      : {
          ...frenteDaQualidade,
          services: frenteDaQualidade.services.filter(
            (service) => service.serviceId === front.serviceId,
          ),
        };
  const servico = frente?.services[0];
  const naoConformidades = quality?.nonConformities ?? [];
  // A medição da equipe é a dona das NCs dela (Fase 37, RN-474); a que ainda vai nascer não
  // tem nenhuma.
  const dono = medida === undefined ? NEW_MEASUREMENT_OWNER : `T:${medida.id}`;

  const rascunho: FrontDraft =
    rascunhoEditado ??
    (frente === undefined
      ? {}
      : initialFrontDraft(frente, naoConformidades, front.serviceId, () => dono));
  const tocado = rascunhoEditado !== undefined;

  // ── A quantidade ────────────────────────────────────────────────────────────────
  const bruto = valor.trim();
  const numero = bruto === '' ? null : Number(bruto.replace(',', '.'));
  const erro = validarQuantidade(numero, unidade?.decimals);
  const valido = erro === undefined;
  // Regravar o mesmo número não é medição (RN-218): não pede checklist, nem é enviado.
  const mudou = valido && numero !== null && numero !== (medida?.quantity ?? null);

  // ── A qualidade (RN-218) ────────────────────────────────────────────────────────
  const exigida = mudou && servico !== undefined;
  const jaVerificada = frente !== undefined && frontHasVerification(frente);
  const completa = frente !== undefined && isFrontDraftComplete(frente, rascunho);
  // O que vai junto: o que a pessoa respondeu, ou — quando a quantidade é nova e ninguém
  // verificou esta frente hoje — a resposta que a cobre. Verificação de antes não é reenviada.
  const enviar = servico !== undefined && (tocado || (exigida && !jaVerificada));
  const carregando = quality === undefined && mudou;
  const qualidadePronta = !carregando && (!enviar || completa);
  const coberta = tocado ? completa : jaVerificada;

  const pecasMexidas = pecas !== undefined;
  const podeSalvar =
    !isSaving && valido && numero !== null && (mudou || tocado || pecasMexidas) && qualidadePronta;

  const salvar = () => {
    if (numero === null) return;
    const fotos = enviar && frente !== undefined ? pendingPhotos(frente, rascunho) : [];
    onConfirm({
      quantity: numero,
      verifications: enviar && frente !== undefined ? frontDraftToInput(frente, rascunho) : [],
      ...(pecas === undefined ? {} : { pieceNames: pecas, previousPieceNames: pecasVistas }),
      ...(fotos.length === 0 ? {} : { photos: fotos }),
    });
  };

  /** O teto da quantidade não conforme: o que a equipe mede, como está no campo (RN-473). */
  const medicaoDaEquipe = (): StepMeasurement => ({
    ownerKey: dono,
    measured: valido && numero !== null ? numero : undefined,
    unitName: unidade?.name,
    unitCode: unidade?.code,
    unitDecimals: unidade?.decimals,
    // As peças marcadas agora, antes de gravar: a NC pode apontá-las (Fase 37, D9).
    draftPieces: pecas,
  });

  // O selo do passo 1: o rascunho, se a pessoa mexeu; senão o que quem verificou antes gravou.
  const resumo =
    frente === undefined
      ? SEM_RESPOSTAS
      : tocado
        ? summarizeFrontDraft(frente, rascunho)
        : (servico?.verification?.summary ?? SEM_RESPOSTAS);

  // Na checklist só se entra com a frente lida: se a qualidade sumir no meio (revalidação), a
  // caixa volta sozinha para a quantidade em vez de mostrar um passo vazio.
  const verificando = passo === 'verificar' && frente !== undefined;
  const nomeDoServico = `${front.activityLabel} · ${front.serviceName}`;

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          {verificando ? (
            <ClipboardCheck className="size-5" aria-hidden="true" />
          ) : (
            <Ruler className="size-5" aria-hidden="true" />
          )}
          {verificando ? 'Verificação de qualidade' : 'Medição da equipe'}
        </DialogTitle>
        <DialogDescription>
          {team.teamName} · {dateLabel}
          {verificando
            ? ' — o serviço executado e a checklist dele.'
            : ' — o que a equipe rendeu nesta frente e serviço, e as fotos que comprovam.'}
        </DialogDescription>
      </DialogHeader>

      {verificando ? (
        <div className="relative flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto">
          <QualityVerificationStep
            serviceId={front.serviceId}
            front={frente}
            draft={rascunho}
            nonConformities={naoConformidades}
            disabled={isSaving}
            photos={nonConformityPhotos}
            nonConformityOwners={quality?.nonConformityOwners}
            measurementOf={medicaoDaEquipe}
            pendingNonConformities={quality?.pendingNonConformities}
            onChange={setRascunhoEditado}
            onBack={() => setPasso('medir')}
          />
        </div>
      ) : (
        <div className="relative flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto">
          <section className="flex flex-col gap-3">
            <div className="min-w-0">
              <p className="break-words text-sm font-medium">{front.activityLabel}</p>
              <p className="text-xs text-(--color-content-muted)">
                {front.towerName} · {front.floorName}
              </p>
              <p className="break-words text-sm">{front.serviceName}</p>
            </div>

            <div className="flex flex-col gap-1.5">
              <h3 className="text-sm font-medium">Quem trabalhou</h3>
              <TeamFrontMembers members={front.members} />
              <p className="text-xs text-(--color-content-muted)">
                {Hours.ofQuarters(front.quarterHours).format()} no dia. As horas, a ausência e a
                hora extra se ajustam no cartão de cada pessoa.
              </p>
            </div>

            {unidade === undefined ? (
              <p className="text-sm text-(--color-content-muted)">
                O serviço não tem unidade — cadastre em Configurações → Serviços para medir.
              </p>
            ) : (
              <Field
                label={`Quantidade medida (${unidade.name})`}
                htmlFor="medicao-equipe-quantidade"
                hint={
                  front.plannedQuantity === undefined
                    ? 'Sem meta lançada para a equipe.'
                    : `Meta da equipe ${formatTeamQuantity(front.plannedQuantity, unidade)}.`
                }
                error={erro}
              >
                <div className="flex gap-2">
                  <Input
                    id="medicao-equipe-quantidade"
                    type="number"
                    inputMode="decimal"
                    step={10 ** -unidade.decimals}
                    min={0}
                    disabled={isSaving}
                    value={valor}
                    onChange={(event) => setValor(event.target.value)}
                  />
                  {/* O m² — alvenaria, reboco, gesso… — mede pelo pano (RN-379). */}
                  {isAreaUnitCode(unidade.code) && (
                    <AreaMeasurementButton
                      subject={nomeDoServico}
                      unitName={unidade.name}
                      decimals={unidade.decimals}
                      disabled={isSaving}
                      onApply={(total) => setValor(String(total))}
                    />
                  )}
                </div>
              </Field>
            )}

            {unidade !== undefined && pieces !== undefined && (
              <PecasDoDia
                activityId={front.activityId}
                serviceId={front.serviceId}
                date={pieces.date}
                load={pieces.load}
                unit={unidade}
                selected={pecas}
                disabled={isSaving}
                onChange={({ pieceNames, total, previous }) => {
                  setPecas(pieceNames);
                  setPecasVistas(previous);
                  // A soma preenche o campo, que continua editável (RN-382).
                  const preenchido = total === undefined ? '0' : String(total);
                  if (total !== undefined || valor === doPreenchimento) {
                    setValor(preenchido);
                    setDoPreenchimento(preenchido);
                  }
                }}
              />
            )}

            {medida !== undefined && (
              <p className="break-words text-xs text-(--color-content-muted)">
                {describeTeamMeasurement(medida, unidade)}
              </p>
            )}

            <EstadoDaQualidade
              carregando={quality === undefined}
              temServico={servico !== undefined}
              temChecklist={servico !== undefined && servico.checklists.length > 0}
              coberta={coberta}
              tocado={tocado}
              exigida={exigida}
              resumo={resumo}
              nomeDoServico={nomeDoServico}
              onVerificar={() => setPasso('verificar')}
            />
          </section>

          {photos}
        </div>
      )}

      {errorMessage !== undefined && (
        <p role="alert" className="text-sm text-(--color-state-danger)">
          {errorMessage}
        </p>
      )}

      <DialogFooter>
        {verificando ? (
          <Button variant="primary" className="min-h-11" onClick={() => setPasso('medir')}>
            Pronto — voltar à quantidade
          </Button>
        ) : (
          <>
            {medida !== undefined && (
              <Button
                variant="ghost"
                className="mr-auto min-h-11 text-(--color-state-danger) md:min-h-9"
                disabled={isSaving}
                onClick={() => setRemovendo(true)}
              >
                <Trash2 aria-hidden="true" />
                Remover medição
              </Button>
            )}
            <Button variant="secondary" onClick={() => onOpenChange(false)}>
              Fechar
            </Button>
            <div className="flex min-w-0 flex-col items-stretch gap-1 sm:items-end sm:text-right">
              <Button
                variant="primary"
                className="min-h-11"
                disabled={!podeSalvar}
                onClick={salvar}
              >
                {isSaving ? 'Salvando…' : 'Salvar medição'}
              </Button>
              {!qualidadePronta ? (
                <p className="text-xs text-(--color-content-muted)">
                  {carregando
                    ? 'Carregando a verificação de qualidade…'
                    : 'Responda a checklist do serviço medido antes de gravar.'}
                </p>
              ) : (
                bruto === '' &&
                medida !== undefined && (
                  <p className="text-xs text-(--color-content-muted)">
                    Em branco não grava — para tirar a medição, use Remover medição.
                  </p>
                )
              )}
            </div>
          </>
        )}
      </DialogFooter>

      <ConfirmDialog
        open={removendo}
        onOpenChange={setRemovendo}
        title="Remover a medição?"
        description={
          medida === undefined
            ? ''
            : `Tira a medição de ${formatTeamQuantity(medida.quantity, unidade)} da ${teamWorkerName(team.teamName)} em ${front.serviceName} neste dia. A frente volta a ficar sem medição, e dá para medir de novo.`
        }
        confirmLabel="Remover medição"
        destructive
        onConfirm={() => {
          setRemovendo(false);
          onConfirm({ quantity: null, verifications: [] });
        }}
      />
    </>
  );
}

/** A mesma regra do contrato (`recordTeamMeasurementSchema`) e da unidade (RN-020), em palavras. */
function validarQuantidade(numero: number | null, casas: number | undefined): string | undefined {
  if (numero === null) return undefined;
  if (!Number.isFinite(numero)) return 'Quantidade inválida.';
  if (numero < 0) return 'A quantidade não pode ser negativa.';
  if (numero > QUANTIDADE_MAXIMA) return 'Quantidade alta demais.';
  if (casas !== undefined && roundQuantity(numero, casas) !== numero) {
    return casas === 0
      ? 'Esta unidade só aceita número inteiro.'
      : `Esta unidade aceita no máximo ${String(casas)} ${casas === 1 ? 'casa decimal' : 'casas decimais'}.`;
  }
  return undefined;
}

/**
 * O estado da verificação do serviço medido, sob o campo de quantidade.
 *
 * Diz em uma linha se há checklist para responder, se a frente já está coberta — pelo rascunho
 * ou por quem verificou antes —, e se a quantidade nova está esperando por isso.
 */
function EstadoDaQualidade({
  carregando,
  temServico,
  temChecklist,
  coberta,
  tocado,
  exigida,
  resumo,
  nomeDoServico,
  onVerificar,
}: {
  carregando: boolean;
  temServico: boolean;
  temChecklist: boolean;
  coberta: boolean;
  tocado: boolean;
  exigida: boolean;
  resumo: { approved: number; rejected: number; total: number };
  nomeDoServico: string;
  onVerificar: () => void;
}) {
  if (carregando) {
    return (
      <p className="text-xs text-(--color-content-muted)">Carregando a verificação de qualidade…</p>
    );
  }
  if (!temServico) {
    return (
      <p className="text-xs text-(--color-content-muted)">
        Sem checklist cadastrada para este serviço — mede como sempre.
      </p>
    );
  }
  if (!temChecklist) {
    return (
      <p className="text-xs text-(--color-content-muted)">
        Este serviço não tem checklist: a medição declara que ele foi executado.
      </p>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {coberta ? (
        <Badge tone={resumo.rejected > 0 ? 'warn' : 'ok'}>
          {resumo.total === 0
            ? 'Serviço declarado'
            : `Qualidade ${String(resumo.approved)}/${String(resumo.total)}${resumo.rejected > 0 ? ` · ${String(resumo.rejected)} NC` : ''}`}
          {tocado && ' (a gravar)'}
        </Badge>
      ) : exigida ? (
        <Badge tone="warn">Verificação pendente — obrigatória para gravar quantidade nova</Badge>
      ) : (
        <Badge tone="neutral">Sem verificação</Badge>
      )}
      <Button
        variant="secondary"
        size="sm"
        className="min-h-11 md:min-h-9"
        onClick={onVerificar}
        aria-label={`Verificar qualidade de ${nomeDoServico}`}
      >
        <ClipboardCheck aria-hidden="true" />
        {coberta ? 'Ver verificação' : 'Verificar qualidade'}
      </Button>
    </div>
  );
}
